import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { Phone, Mail, MapPin, Clock, Truck, Users, CheckCircle2 } from "lucide-react";
import { InnerPage } from "@/components/InnerPage";
import { SectionHeader } from "@/components/SectionHeader";
import { pageHead } from "@/lib/seo";
import { breadcrumbsFor } from "@/lib/schema";
import { SITE } from "@/lib/site";

const PATH = "/contact";

const META_DESCRIPTION = `Book a load or talk to Fox Trucking dispatch 24/7. Request a quote for refrigerated or dry van freight — call ${SITE.phoneDisplay}.`;

export const Route = createFileRoute("/contact")({
  head: () =>
    pageHead({
      title: "Book a Load | Contact Fox Trucking Dispatch 24/7",
      description: META_DESCRIPTION,
      path: PATH,
      ogTitle: "Book a Load | Contact Fox Trucking Dispatch 24/7",
      ogDescription: META_DESCRIPTION,
    }),
  component: ContactPage,
});

const CONTACT_FAQS = [
  {
    question: "How fast will I get a quote?",
    // TODO: Confirm published quote turnaround SLA.
    answer:
      "[QUOTE TURNAROUND — confirm typical response time]. Call dispatch at " +
      SITE.phoneDisplay +
      " if you need a same-day answer.",
  },
  {
    question: "What do you need to quote a load?",
    answer:
      "Company and contact details, pickup and delivery city/state, freight type (reefer, dry van, or other), pickup date, weight, and any special notes — the form on this page covers it.",
  },
  {
    question: "What are your payment terms?",
    // TODO: Confirm payment terms for shippers / brokers.
    answer:
      "[PAYMENT TERMS — placeholder]. Ask dispatch when you book and we’ll confirm in writing with your rate confirmation.",
  },
];

function ContactPage() {
  return (
    <InnerPage
      hero={{
        eyebrow: "Dispatch · 24/7",
        title: "Book a Load or Talk to Dispatch",
        intro: `Shippers: request a quote below. Drivers: apply on our careers page. Either way, a real person answers at ${SITE.phoneDisplay}.`,
        primaryCta: { label: "Request a Quote", href: "#quote" },
        secondaryCta: { label: "Call Dispatch", href: `tel:${SITE.phoneTel}` },
        showPhoneCta: false,
      }}
      breadcrumbs={breadcrumbsFor("Book a Load", PATH)}
      faqs={CONTACT_FAQS}
      faqTitle="Booking questions."
      faqSubtitle="Turnaround, load details, and payment — straight answers for shippers."
      showBottomCta={false}
    >
      {/* TWO PATHS */}
      <section className="bg-background py-16">
        <div className="mx-auto grid max-w-7xl gap-6 px-4 md:grid-cols-2 lg:px-8">
          <a
            href="#quote"
            className="group flex gap-5 rounded-lg border border-border bg-card p-8 transition hover:border-primary"
          >
            <div className="grid h-14 w-14 shrink-0 place-items-center rounded-md bg-primary text-primary-foreground">
              <Truck className="h-7 w-7" aria-hidden="true" />
            </div>
            <div>
              <p className="font-display text-xs tracking-[0.3em] text-primary">Shippers</p>
              <h2 className="mt-2 font-display text-2xl text-card-foreground md:text-3xl">
                Get a freight quote
              </h2>
              <p className="mt-2 text-sm text-muted-foreground">
                Refrigerated, dry van, or other FTL — send load details and we’ll follow up.
              </p>
              <span className="mt-4 inline-flex font-display tracking-wider text-primary group-hover:underline">
                Jump to quote form →
              </span>
            </div>
          </a>
          <Link
            to="/drive-with-us"
            className="group flex gap-5 rounded-lg border border-border bg-card p-8 transition hover:border-primary"
          >
            <div className="grid h-14 w-14 shrink-0 place-items-center rounded-md bg-accent text-accent-foreground">
              <Users className="h-7 w-7" aria-hidden="true" />
            </div>
            <div>
              <p className="font-display text-xs tracking-[0.3em] text-primary">Drivers</p>
              <h2 className="mt-2 font-display text-2xl text-card-foreground md:text-3xl">
                Drive with Fox Trucking
              </h2>
              <p className="mt-2 text-sm text-muted-foreground">
                Class A CDL careers — apply on the driver page or call recruiting.
              </p>
              <span className="mt-4 inline-flex font-display tracking-wider text-primary group-hover:underline">
                Go to Drive With Us →
              </span>
            </div>
          </Link>
        </div>
      </section>

      {/* QUOTE FORM + CONTACT BLOCK */}
      <section id="quote" className="scroll-mt-20 bg-muted py-24">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <SectionHeader
            eyebrow="Quote"
            title="Tell us about the load."
            subtitle="Fill in the details below — your email app opens with a ready message, or call dispatch anytime."
          />
          <div className="mt-14 grid gap-10 lg:grid-cols-[1fr_1.4fr]">
            <ContactInfoBlock />
            <QuoteForm />
          </div>
        </div>
      </section>
    </InnerPage>
  );
}

function ContactInfoBlock() {
  return (
    <div className="space-y-4">
      <ContactCard
        icon={Phone}
        label="Call Dispatch"
        value={SITE.phoneDisplay}
        href={`tel:${SITE.phoneTel}`}
      />
      <ContactCard icon={Mail} label="Email" value={SITE.email} href={`mailto:${SITE.email}`} />
      <ContactCard icon={MapPin} label="Address" value={SITE.address} />
      <ContactCard icon={Clock} label="Hours" value="Dispatch available 24/7" />
    </div>
  );
}

function ContactCard({
  icon: Icon,
  label,
  value,
  href,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
  href?: string;
}) {
  const inner = (
    <div className="flex items-start gap-4 rounded-lg border border-border bg-card p-6 transition hover:border-primary">
      <div className="grid h-12 w-12 shrink-0 place-items-center rounded-md bg-primary text-primary-foreground">
        <Icon className="h-5 w-5" aria-hidden="true" />
      </div>
      <div>
        <div className="font-display text-xs tracking-widest text-muted-foreground">
          {label.toUpperCase()}
        </div>
        <div className="mt-1 font-display text-lg text-card-foreground">{value}</div>
      </div>
    </div>
  );
  if (href) return <a href={href}>{inner}</a>;
  return inner;
}

function parseQuote(raw: Record<string, FormDataEntryValue>) {
  const company = String(raw.company ?? "").trim();
  const contactName = String(raw.contactName ?? "").trim();
  const phone = String(raw.phone ?? "").trim();
  const email = String(raw.email ?? "").trim();
  const pickup = String(raw.pickup ?? "").trim();
  const delivery = String(raw.delivery ?? "").trim();
  const freightType = String(raw.freightType ?? "").trim();
  const pickupDate = String(raw.pickupDate ?? "").trim();
  const weight = String(raw.weight ?? "").trim();
  const notes = String(raw.notes ?? "").trim();

  if (!company || company.length > 120) return { error: "Company name required" };
  if (!contactName || contactName.length > 120) return { error: "Contact name required" };
  if (!phone || phone.length > 30) return { error: "Phone required" };
  if (!email || email.length > 255 || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    return { error: "Valid email required" };
  }
  if (!pickup || pickup.length > 120) return { error: "Pickup city/state required" };
  if (!delivery || delivery.length > 120) return { error: "Delivery city/state required" };
  if (!["refrigerated", "dry-van", "other"].includes(freightType)) {
    return { error: "Select a freight type" };
  }
  if (!pickupDate) return { error: "Pickup date required" };
  if (!weight || weight.length > 60) return { error: "Weight required" };
  if (notes.length > 1500) return { error: "Notes are too long" };

  return {
    data: {
      company,
      contactName,
      phone,
      email,
      pickup,
      delivery,
      freightType,
      pickupDate,
      weight,
      notes,
    },
  };
}

function freightTypeLabel(value: string) {
  if (value === "refrigerated") return "Refrigerated";
  if (value === "dry-van") return "Dry van";
  return "Other";
}

function QuoteForm() {
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  function submit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const parsed = parseQuote(Object.fromEntries(new FormData(form)));
    if ("error" in parsed) {
      setSuccess(false);
      setStatus(parsed.error ?? "Please check the form");
      return;
    }

    setLoading(true);
    setSuccess(false);
    const d = parsed.data;
    const lines = [
      `Company: ${d.company}`,
      `Contact: ${d.contactName}`,
      `Phone: ${d.phone}`,
      `Email: ${d.email}`,
      `Pickup: ${d.pickup}`,
      `Delivery: ${d.delivery}`,
      `Freight type: ${freightTypeLabel(d.freightType)}`,
      `Pickup date: ${d.pickupDate}`,
      `Weight: ${d.weight}`,
      d.notes ? `Notes: ${d.notes}` : null,
    ]
      .filter(Boolean)
      .join("%0D%0A");

    window.location.href = `mailto:${SITE.email}?subject=${encodeURIComponent(
      `Freight Quote — ${d.company}`,
    )}&body=${lines}`;

    setSuccess(true);
    setStatus(
      `Thanks, ${d.contactName}. Your email app is opening with this quote request — hit send, or call ${SITE.phoneDisplay}.`,
    );
    form.reset();
    setTimeout(() => setLoading(false), 600);
  }

  if (success && status) {
    return (
      <div className="rounded-lg border border-accent/40 bg-card p-8 text-center" role="status">
        <CheckCircle2 className="mx-auto h-10 w-10 text-primary" aria-hidden="true" />
        <h3 className="mt-4 font-display text-2xl text-card-foreground">Quote request ready</h3>
        <p className="mt-2 text-sm text-muted-foreground">{status}</p>
        <button
          type="button"
          className="mt-6 inline-flex h-12 items-center justify-center rounded-md bg-primary px-6 font-display tracking-wider text-primary-foreground hover:bg-primary/90"
          onClick={() => {
            setSuccess(false);
            setStatus(null);
          }}
        >
          Submit another
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      className="rounded-lg border border-border bg-card p-8 shadow-sm"
      noValidate
    >
      <div className="grid gap-4 md:grid-cols-2">
        <Field label="Company Name" name="company" required autoComplete="organization" />
        <Field label="Contact Name" name="contactName" required autoComplete="name" />
        <Field label="Phone" name="phone" type="tel" required autoComplete="tel" />
        <Field label="Email" name="email" type="email" required autoComplete="email" />
        <Field
          label="Pickup City / State"
          name="pickup"
          required
          autoComplete="off"
          placeholder="e.g. Newark, NJ"
        />
        <Field
          label="Delivery City / State"
          name="delivery"
          required
          autoComplete="off"
          placeholder="e.g. Miami, FL"
        />
        <div>
          <label
            htmlFor="quote-freightType"
            className="font-display text-xs tracking-widest text-muted-foreground"
          >
            FREIGHT TYPE <span className="text-primary">*</span>
          </label>
          <select
            id="quote-freightType"
            name="freightType"
            required
            defaultValue=""
            className="mt-2 w-full rounded-md border border-input bg-background px-4 py-3 text-sm focus:border-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <option value="" disabled>
              Select freight type
            </option>
            <option value="refrigerated">Refrigerated</option>
            <option value="dry-van">Dry van</option>
            <option value="other">Other</option>
          </select>
        </div>
        <Field label="Pickup Date" name="pickupDate" type="date" required autoComplete="off" />
        <Field
          label="Weight"
          name="weight"
          required
          autoComplete="off"
          placeholder="e.g. 42,000 lbs"
        />
      </div>
      <div className="mt-4">
        <label
          htmlFor="quote-notes"
          className="font-display text-xs tracking-widest text-muted-foreground"
        >
          NOTES
        </label>
        <textarea
          id="quote-notes"
          name="notes"
          rows={5}
          maxLength={1500}
          placeholder="Commodity, temperature setpoint, appointment times, special requirements..."
          autoComplete="off"
          className="mt-2 w-full rounded-md border border-input bg-background px-4 py-3 text-sm focus:border-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
      </div>
      <button
        disabled={loading}
        type="submit"
        className="mt-6 w-full rounded-md bg-primary px-6 py-4 font-display tracking-widest text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
      >
        {loading ? "Opening email..." : "Send Quote Request by Email"}
      </button>
      {status && !success && (
        <p className="mt-3 text-center text-sm text-primary" role="alert">
          {status}
        </p>
      )}
      <p className="mt-3 text-center text-xs text-muted-foreground">
        This opens your email app with the details filled in. Prefer to talk? Call{" "}
        <a href={`tel:${SITE.phoneTel}`} className="underline-offset-2 hover:underline">
          {SITE.phoneDisplay}
        </a>
        .
      </p>
    </form>
  );
}

function Field({
  label,
  name,
  type = "text",
  required,
  autoComplete = "off",
  placeholder,
}: {
  label: string;
  name: string;
  type?: string;
  required?: boolean;
  autoComplete?: string;
  placeholder?: string;
}) {
  const id = `quote-${name}`;
  return (
    <div>
      <label htmlFor={id} className="font-display text-xs tracking-widest text-muted-foreground">
        {label.toUpperCase()}
        {required && <span className="text-primary"> *</span>}
      </label>
      <input
        id={id}
        type={type}
        name={name}
        required={required}
        maxLength={255}
        autoComplete={autoComplete}
        placeholder={placeholder}
        className="mt-2 w-full rounded-md border border-input bg-background px-4 py-3 text-sm focus:border-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      />
    </div>
  );
}
